import { create } from 'zustand'

import type { Part } from '@/services/schemas'
import { $data } from '@/stores/Data'
import { ITEMS_PRESELECTED, POWER_CATEGORY, SENSOR_CATEGORY } from '@/util/Hardcoded'

export type Limit = {
  hubId: string
  categoryId: string
  limit: number
  amount: number
  hit: boolean
}

type LimitStore = {
  limits: Limit[]
  initialize: () => void
  reset: () => void
  setLimits: (limits: Limit[]) => void
  increase: (categoryId: string, hubId: string) => void
  decrease: (categoryId: string, hubId: string) => void
}

const generateLimitsFromJSON = (): Limit[] => {
  const data = $data.get()!
  const limits: Limit[] = []

  data.hubs.forEach((hub) => {
    hub.categories.forEach((category) => {
      const parts = category.parts as Part[]

      const amount = parts.filter(part => ITEMS_PRESELECTED.includes(part.id))
      const limit = category.id === SENSOR_CATEGORY
        ? Number.parseInt(hub.amountLimitSensors)
        : category.id === POWER_CATEGORY
          ? Number.parseInt(hub.amountLimitPSUs)
          : 999

      limits.push({
        hubId: hub.id,
        categoryId: category.id,
        limit,
        amount: amount.length,
        hit: amount.length >= limit,
      })
    })
  })

  return limits
}

const useLimitStore = create<LimitStore>()((set, get) => ({
  limits: [],

  initialize: () => get().reset(),
  reset: () => set({
    limits: generateLimitsFromJSON(),
  }),
  setLimits: limits => set({
    limits,
  }),

  increase: (categoryId, hubId) => set((state) => {
    const index = state.limits.findIndex(limit => limit.categoryId === categoryId && limit.hubId === hubId)
    if (!state.limits[index]) {
      return state
    }
    const oldItem = state.limits[index]
    const newAmount = oldItem.amount + 1
    const item = {
      ...oldItem,
      amount: newAmount,
      hit: newAmount >= oldItem.limit,
    }

    state.limits[index] = item
    return { ...state, limits: [...state.limits] }
  }),

  decrease: (categoryId, hubId) => set((state) => {
    const index = state.limits.findIndex(limit => limit.categoryId === categoryId && limit.hubId === hubId)
    if (!state.limits[index]) {
      return state
    }
    const oldItem = state.limits[index]
    const newAmount = oldItem.amount - 1
    const item = {
      ...oldItem,
      amount: newAmount,
      hit: newAmount >= oldItem.limit,
    }

    state.limits[index] = item
    return { ...state, limits: [...state.limits] }
  }),
}))

export default useLimitStore
